<template>
  <div>
    <SelectorPanel
      type="places"
      text="Places"
      :dialog-full-height="true"
      :dialog-width="300"
      :disabled="disabled"
      :force-close="forceClose"
    >
      <SelectorPanelHeader
        type="places"
        title="Places"
        @click-close="handleClickClose"
      ></SelectorPanelHeader>

      <div v-if="!hasPlaces || isUsingDefaultPlaces">
        <p>
          Seerist's Places allows you to view locations throughout the world
          that matter to you.
        </p>
        <p>
          View all of your worldwide offices, your important global assests or
          just specific regional points of interest.
        </p>
        <p>
          <img :src="supportImg" class="hyp-places-support-img" />
        </p>
        <p>
          If you would like to add your Places to Seerist please contact the
          client success team at
          <a href="mailto:help@seerist.com">help@seerist.com</a>.
        </p>
      </div>
      <PlacesList
        v-if="hasPlaces"
        :places="places"
        @change="placeOnChange"
      ></PlacesList>
    </SelectorPanel>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashFind from "lodash/find";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashDebounce from "lodash/debounce";

import { mapGetters } from "vuex";
import {
  GET_DETAILS_SIDEBAR,
  GET_PLACES,
  GET_PLACES_REFRESH_MAP,
  SAVE_PREFERENCES,
} from "@/store/actions.type";
import { SET_PLACES_DETAILS } from "@/store/mutations.type";
import {
  MODULE_PREFIX_PLACES,
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_USER,
} from "@/store";
import { mapMarkerData, removeMapData } from "@/common/maps/features";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { getDefaultMarkerOptions } from "@/common/maps/styles";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import { pageTrackerLogger } from "@/common/pageTracker";

export default {
  name: "PlacesSelector",
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    SelectorPanel: () => import("@/components/common/SelectorPanel"),
    SelectorPanelHeader: () =>
      import("@/components/common/SelectorPanelHeader"),
    PlacesList: () => import("@/components/places/PlacesList"),
  },
  data: () => ({
    forceClose: null,
  }),
  async mounted() {
    await this.getPlaces();
  },
  computed: {
    ...mapGetters({
      places: MODULE_PREFIX_PLACES + "places",
      refreshMap: MODULE_PREFIX_PLACES + "refreshMap",
      maps: MODULE_PREFIX_MAP + "maps",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
      preferences: MODULE_PREFIX_USER + "preferences",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    hasPlaces() {
      if (this.places && this.places.length) {
        return true;
      }
      return false;
    },
    isUsingDefaultPlaces() {
      for (let i = 0; i < this.places.length; i++) {
        if (this.places[i].isDefaultLayer) {
          return true;
        }
      }
      return false;
    },
    supportImg() {
      return require("@/assets/images/miscellaneous/places_example.png");
    },
  },
  methods: {
    handleClickClose() {
      this.forceClose = new Date().toISOString();
    },
    async getPlaces() {
      await this.$store.dispatch(MODULE_PREFIX_PLACES + GET_PLACES);
    },
    placeOnChange(inPlace) {
      let place = lodashFind(this.places, ["uniqueId", inPlace.uniqueId]);
      place.mapped = inPlace.mapped;
      this.updatePlacesOnMap();
      this.savePrefs();
    },
    isMapAvailable() {
      if (this.map) {
        if (
          typeof this.map !== "undefined" &&
          this.map &&
          typeof this.map.getStyle() !== "undefined"
        ) {
          return true;
        }
      }
      return false;
    },
    updatePlacesOnMap: lodashDebounce(function () {
      if (this.isMapAvailable()) {
        if (
          this.isAvailableWebGL() &&
          this.places &&
          Array.isArray(this.places) &&
          this.places.length
        ) {
          this.places.forEach((place) => {
            removeMapData(this.map, this.getLayerId(place.uniqueId));
            if (place.mapped && lodashGet(place, "data.features")) {
              this.addToMap(place);
            }
          });
        }
      }
    }, 1500),
    getLayerId(id) {
      return `places-${id}`;
    },
    addToMap(place) {
      const LAYER_ID = this.getLayerId(place.uniqueId);
      const SOURCE = {
        type: "FeatureCollection",
        features: place.data.features,
      };

      let iconName = "default";
      if (lodashGet(place, "icons")) {
        iconName = place.icons;
      }

      const PARAMS = {
        id: LAYER_ID,
        noCluster: true,
        iconName: iconName,
      };

      const OPTIONS = getDefaultMarkerOptions(PARAMS);

      mapMarkerData({
        id: LAYER_ID,
        data: SOURCE,
        mapClickEvent: this.mapClickEvent,
        markerOptions: OPTIONS,
        map: this.map,
        noCluster: true,
      });

      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.places,
        EVENT_ACTIONS.addDataToMap,
        place.name
      );
    },
    async mapClickEvent(e, features) {
      e.originalEvent.stopPropagation();
      if (features.length > 0) {
        this.detailsSidebar.visible = true;
        this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.places;
        this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.places;

        const NAME = this.getLayerName(features[0]);

        const PARAMS = {
          title: NAME,
          feature: features[0],
        };
        this.$store.commit(MODULE_PREFIX_PLACES + SET_PLACES_DETAILS, PARAMS);

        await this.$store.dispatch(
          MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
          this.detailsSidebar
        );
      }
    },
    getLayerName(feature) {
      if (feature && feature.source) {
        const PARTS = feature.source.split("places-");
        if (PARTS && Array.isArray(PARTS) && PARTS[1]) {
          const PLACE = lodashFind(lodashGet(this.preferences, "places"), [
            "uniqueId",
            PARTS[1],
          ]);
          if (PLACE && PLACE.name) {
            return PLACE.name;
          }
        }
      }
      return null;
    },
    getPrefs() {
      if (
        this.places &&
        Array.isArray(this.places) &&
        lodashGet(this.preferences, "places")
      ) {
        this.places.forEach((place) => {
          const PLACE = lodashFind(lodashGet(this.preferences, "places"), [
            "uniqueId",
            place.uniqueId,
          ]);
          place.mapped = false;
          if (PLACE && PLACE.mapped) {
            place.mapped = true;
          }
        });
      }
    },
    savePrefs() {
      let preferences = this.preferences;
      preferences.places = lodashCloneDeep(this.places);
      preferences.places.forEach((place) => {
        if (place["data"]) {
          delete place["data"];
        }
        if (place["color"]) {
          delete place["color"];
        }
      });
      this.$store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, preferences);
      preferences = null;
    },
    handlePageTrackerEvent(category, action, name) {
      let _paq = (window._paq = window._paq || []);
      _paq.push(["trackEvent", category, action, name]);
      pageTrackerLogger(
        "Page Tracker Event " +
          category +
          " " +
          action +
          " " +
          name +
          " logged."
      );
    },
  },
  watch: {
    refreshMap: {
      async handler() {
        if (this.refreshMap) {
          await this.getPlaces();
          this.$store.dispatch(
            MODULE_PREFIX_PLACES + GET_PLACES_REFRESH_MAP,
            false
          );
        }
      },
    },
    places: {
      handler() {
        this.getPrefs();
        this.updatePlacesOnMap();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.hyp-places-support-img {
  width: 235px;
}
</style>
